import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { changelogAPI } from '../lib/api';
import { 
  FileText, 
  Calendar,
  User,
  Key
} from 'lucide-react';

export default function ChangelogPage() {
  const [changelog, setChangelog] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadChangelog() {
      try {
        const response = await changelogAPI.get();
        setChangelog(response.data);
      } catch (error) {
        console.error('Failed to load changelog:', error);
      } finally {
        setLoading(false);
      }
    }
    loadChangelog();
  }, []);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8" data-testid="changelog-loading">
        <div className="space-y-4">
          {Array(3).fill(0).map((_, i) => (
            <div key={i} className="loading-pulse h-32 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="changelog-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-2 text-muted-foreground text-sm mb-2">
          <FileText className="h-4 w-4" />
          <span>Governance</span>
        </div>
        <h1 className="text-3xl font-semibold mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
          Change Log
        </h1>
        <p className="text-muted-foreground">
          Version history and amendments to the Canon
        </p>
      </div>

      {/* Explanation Card */}
      <Card className="mb-8 bg-muted/30">
        <CardContent className="pt-6">
          <h3 className="font-medium mb-2">Transparent Governance</h3>
          <p className="text-sm text-muted-foreground">
            All changes to the Canon are documented here with version numbers, dates, 
            descriptions, and cryptographic signatures. This ensures accountability 
            and allows anyone to verify the authenticity and history of the document.
          </p>
        </CardContent>
      </Card>

      {/* Changelog Entries */}
      <div className="space-y-6" data-testid="changelog-list">
        {changelog.map((entry, index) => (
          <Card key={entry.version} data-testid={`changelog-entry-${entry.version}`}>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center space-x-2">
                  <Badge className="text-sm">v{entry.version}</Badge>
                  {index === 0 && (
                    <Badge variant="secondary">Current</Badge>
                  )}
                </div>
                <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                  <div className="flex items-center space-x-1">
                    <Calendar className="h-4 w-4" />
                    <span>{entry.date}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <User className="h-4 w-4" />
                    <span>{entry.author}</span>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <h4 className="font-medium mb-3">Changes</h4>
              <ul className="space-y-2">
                {entry.changes.map((change, i) => (
                  <li key={i} className="flex items-start space-x-2 text-sm">
                    <span className="text-accent">•</span>
                    <span>{change}</span>
                  </li>
                ))}
              </ul>

              <Separator className="my-4" />

              <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                <Key className="h-3 w-3" />
                <span className="font-mono">{entry.signature}</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Amendment Process */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle className="text-lg">Amendment Process</CardTitle>
          <CardDescription>How changes are proposed and approved</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="p-4 rounded-lg bg-muted/50">
              <h4 className="font-medium mb-2">Regular Amendments</h4>
              <p className="text-muted-foreground">
                Titles III-IX can be amended with standard multi-signature approval 
                and public reason requirement (Article 25).
              </p>
            </div>
            <div className="p-4 rounded-lg bg-muted/50">
              <h4 className="font-medium mb-2">Protected Sections</h4>
              <p className="text-muted-foreground">
                Foundational Principles (Title I) and Core Rights (Title II) require 
                supermajority and multi-layer review (Article 24).
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
